import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, MapPin, Clock, Calendar, Star, ExternalLink, DollarSign, User } from 'lucide-react';
import { eventApi } from '../services/api';
import { format, parseISO } from 'date-fns';
import ShareButtons from '../components/ShareButtons';
import Linkify from '../components/Linkify';

const EventDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvent();
  }, [slug]);

  const loadEvent = async () => {
    try {
      const response = await eventApi.getById(slug);
      setEvent(response.data);
      if (response.data.slug && response.data.slug !== slug) {
        navigate(`/events/${response.data.slug}`, { replace: true });
      }
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatEventDate = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Event not found</h2>
          <Link to="/events" className="btn-primary">Back to Events</Link>
        </div>
      </div>
    );
  }

  const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
  const pageUrl = `${siteUrl}/events/${event.slug || slug}`;
  const ogImage = event.image 
    ? `${siteUrl}${event.image}` 
    : `${siteUrl}/og-image.png`;

  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.event_name,
    "description": event.description || "",
    "url": pageUrl,
    "image": ogImage,
    ...(event.event_date && event.start_time
      ? { startDate: `${event.event_date}T${event.start_time}` }
      : event.event_date && { startDate: event.event_date }),
    ...(event.end_date && event.end_time 
      ? { endDate: `${event.end_date}T${event.end_time}` }
      : event.end_date && { endDate: event.end_date }),
    ...(event.location && {
      location: {
        "@type": "Place",
        "name": event.location,
        "address": { "@type": "PostalAddress", "addressLocality": "Martinsville", "addressRegion": "VA" }
      }
    }),
    ...(event.is_free && { isAccessibleForFree: true }),
    "organizer": { "@type": "Organization", "name": "Explore MHC", "url": siteUrl }
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="event-detail">
      <Helmet>
        <title>{`${event.event_name} | Explore MHC`}</title>
        <meta name="description" content={event.description ? event.description.substring(0, 160) : ''} />
        <meta property="og:type" content="event" />
        <meta property="og:title" content={`${event.event_name} | Explore MHC`} />
        <meta property="og:description" content={event.description ? event.description.substring(0, 200) : ''} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Explore MHC" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${event.event_name} | Explore MHC`} />
        <meta name="twitter:description" content={event.description ? event.description.substring(0, 200) : ''} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/events" className="inline-flex items-center gap-2 text-neutral-600 hover:text-primary transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Events
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Image */}
            {event.image ? (
              <div className="bg-neutral-100 rounded-2xl overflow-hidden flex items-center justify-center">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${event.image}`}
                  alt={event.event_name}
                  className="w-full max-h-[75vh] object-contain"
                />
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-secondary to-secondary-light rounded-2xl flex items-center justify-center">
                <span className="text-9xl font-bold text-white/30">
                  {event.event_name.charAt(0)}
                </span>
              </div>
            )}

            {/* Event Info */}
            <div className="bg-white rounded-2xl p-5 sm:p-8 border border-neutral-200">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div className="min-w-0 flex-1">
                  <span className="badge badge-category mb-3">{event.category}</span>
                  <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-2 break-words" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    {event.event_name}
                  </h1>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap shrink-0">
                  <ShareButtons 
                    type="event" 
                    slug={event.slug || slug}
                    title={event.event_name}
                    description={event.description}
                  />
                  {event.is_featured && (
                    <span className="badge badge-featured flex items-center gap-1 whitespace-nowrap">
                      <Star className="w-4 h-4" /> Featured
                    </span>
                  )}
                </div>
              </div>

              <p className="text-neutral-700 leading-relaxed mb-6 whitespace-pre-wrap break-words">
                <Linkify text={event.description} />
              </p>

              {/* Submitted by */}
              <div className="border-t border-neutral-200 pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-neutral-500" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">{event.submitter_name}</p>
                    {event.organization_name && (
                      <p className="text-sm text-neutral-500">{event.organization_name}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Details Card */}
            <div className="bg-white rounded-2xl p-6 border border-neutral-200">
              <h3 className="font-bold text-lg mb-4">Event Details</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Date</p>
                    <p className="font-medium">
                      {formatEventDate(event.event_date)}
                      {event.end_date && event.end_date !== event.event_date && ` - ${formatEventDate(event.end_date)}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Time</p>
                    <p className="font-medium">{event.start_time} - {event.end_time}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-neutral-500">Location</p>
                    <p className="font-medium break-words">{event.location}</p>
                    {event.location && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:text-primary-dark underline mt-1 inline-block"
                        data-testid="event-directions-link"
                      >
                        Get Directions →
                      </a>
                    )}
                  </div>
                </div>

                {event.cost && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-500">Cost</p>
                      <p className="font-medium">
                        {event.cost === 'Free' || event.cost === '0' ? 'Free Event' : event.cost}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-6 border-t border-neutral-200 space-y-3">
                {event.ticket_url && (
                  <a
                    href={event.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-accent w-full flex items-center justify-center gap-2"
                  >
                    Get Tickets <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                
                <button className="btn-secondary w-full">
                  Add to Calendar
                </button>
              </div>
            </div>

            {/* Google Maps */}
            {event.location && (
              <div className="bg-white rounded-2xl p-6 border border-neutral-200">
                <h3 className="font-bold text-lg mb-4">Location</h3>
                <div className="aspect-square rounded-xl overflow-hidden">
                  <iframe
                    title="Event Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}&output=embed`}
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
